/**
 * CrossMyLoof (Cross My Loof UK Limited, Outwell, Cambridgeshire) — the CML
 * dry brewing range.
 *
 * The maker's site is **crossmyloofbrew.co.uk**, not crossmyloof.co.uk: that
 * shorter domain lapsed and was re-registered through Squarespace on
 * 16 February 2026, and today serves nothing but a "Coming Soon" parking page.
 * crossmyloofbrew.co.uk has been held since 2017, was last edited in August
 * 2026 and is actively selling, so it is the only address used here.
 *
 * Every number below was read in September 2026 off CML's own specification
 * page, https://www.crossmyloofbrew.co.uk/yeast-specs, cross-checked against
 * the strain lists on their store's yeast products.
 *
 * **No entry carries a `url`, and that is deliberate.** CML sell the whole
 * range as dropdown options on three grouped store products — Beer Yeast (Home
 * Brew Packs), 100 g Packs and 500 g Packs — so there is no maker page for an
 * individual strain to link to. The one document that describes each strain,
 * `/yeast-specs`, covers the entire range on one page and is therefore a family
 * page, which `productUrlSchema` refuses. Absent is the honest answer.
 *
 * Four conversions were needed, and each is ours rather than CML's:
 *
 * - **Flocculation.** CML print a graded word for most strains, the word
 *   "Good" for Beòir, Four and Pia (read here as `medium-high`, the
 *   conservative of the two readings), "Med/High" for Flushed Nun, and for the
 *   four strains in the CROSSMYLOOF BREW block a *sedimentation percentage*
 *   instead — 70–72 %, 76–80 %, 70–73 %, 75–78 %. Those all sit in the middle
 *   of the ASBC band and are stored as `medium`.
 * - **Alcohol tolerance.** CML publish no tolerance figure as such; they print
 *   a "max brew strength per sachet in a 23 l brew" (or "in 20 litres"), and
 *   say to double the pitch if more is wanted. That number is what is stored,
 *   because it is the maker's answer to the same question. The Firm is printed
 *   as "8 to 9 %" and the lower bound is kept.
 * - **Attenuation.** Haze is published as a single 75 %, so the band here is
 *   that ±2. Clipper is published as "75 % +/- 5 %", stored as 70–80.
 * - **Dosage.** `dosageGramsPerHl` is filled only where CML print an actual
 *   rate: the BEIRM strains all carry a "Pitching Rate: n g/100 litres" line,
 *   Sour 3.5 a "Dosage: 30 g per 23 l" and New Sour 3.5 a "Use 5 g to 25
 *   litres". The CROSSMYLOOF BREW sachets and the two kveik packs state only
 *   what one pack will brew, which is pack coverage rather than a rate, so
 *   those entries carry no dosage.
 *
 * Marked `representative`, with the reason in each description:
 *
 * - `cml-no-1-voss`, `cml-horn` — CML publish attenuation for the kveik pair
 *   only as "medium to high", so the band is typical for the species.
 * - `cml-sour-3-5`, `cml-new-sour-3-5` — souring pitches, with neither
 *   attenuation nor flocculation published; the attenuation band is nominal,
 *   following `escarpment-lactobacillus-blend-2`.
 *
 * STA1 is `null` throughout bar two: CML declare Gretel and Wallonia as
 * *Saccharomyces cerevisiae* var. *diastaticus* in the ingredient line.
 *
 * Three things are deliberately absent. The WHC Labs strains CML resell
 * (Saturated, LAX, Apres Ski and the rest) are WHC's own products and already
 * live in `whc-lab.ts`. So is Mangrove Jack's M24 Versa Lager, which CML list
 * under Mangrove Jack's name. And the store's CML "Saison" option has no
 * figures anywhere on the site — not on `/yeast-specs`, not on the product
 * listings — so nothing was entered for it rather than inventing a saison.
 */

import type { Yeast } from '../../schema/yeast';

/** CrossMyLoof dry brewing yeast. @see Yeast */
export const crossMyLoofYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // CROSSMYLOOF BREW — the house sachets
  // -------------------------------------------------------------------------
  {
    id: 'cml-heatwave',
    lab: 'CrossMyLoof',
    productCode: 'Heatwave',
    name: 'Heatwave',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 31, max: 35 },
    flocculation: 'high',
    alcoholTolerancePercent: 9.5,
    sta1: null,
    description:
      "CML's answer to brewing without a chiller: a thermotolerant ale strain that holds a clean, restrained-ester profile at 31–35 °C, where most ale yeast turns solventy. It attenuates fully and drops hard, so pale ales, IPAs and stouts fermented through a warm spell still taste as though they were kept cold.",
    tags: ['thermotolerant', 'clean', 'ipa', 'pale-ale', 'warm-ferment', 'high-flocculation'],
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-belgian',
    lab: 'CrossMyLoof',
    productCode: 'Belgian',
    name: 'Belgian',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 77, max: 83 },
    temperatureC: { min: 15, max: 28 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9.5,
    sta1: null,
    description:
      'A phenolic Belgian house strain with clove in front and banana behind it, and a band wide enough that the brewer picks the intensity: cool for a restrained blonde, warm for a full-throated abbey ale. CML publish its settling as a 70–72 % sedimentation figure rather than a graded word.',
    tags: ['belgian', 'phenolic', 'clove', 'banana', 'abbey', 'blonde'],
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-kolsch',
    lab: 'CrossMyLoof',
    productCode: 'Kölsch',
    name: 'Kölsch',
    form: 'dry',
    type: 'hybrid',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A top-cropping lager clone for brewers who want lager character out of an ale fermenter: clean, and without the sulfur a true bottom-fermenting strain throws. CML ask for eight weeks of cold conditioning afterwards, and for a doubled pitch if it is run below 15 °C.',
    tags: ['kolsch', 'lager-like', 'clean', 'cream-ale', 'cold-conditioning'],
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-kristallweizen',
    lab: 'CrossMyLoof',
    productCode: 'Kristallweizen',
    name: 'Kristallweizen',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 79 },
    temperatureC: { min: 15, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A wheat-beer strain that throws banana and clove and still falls bright, which is the whole point of a kristallweizen. The band runs all the way to 35 °C and the esters climb with it; the mouthfeel stays silky at either end.',
    tags: ['hefeweizen', 'wheat', 'banana', 'clove', 'kristallweizen'],
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-ale-cask',
    lab: 'CrossMyLoof',
    productCode: 'Ale (Cask)',
    name: 'Ale (Cask)',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 82 },
    temperatureC: { min: 16, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9.5,
    sta1: null,
    description:
      'CML pitch this one at cask first: a broad-band top fermenter that leaves enough yeast in suspension to condition in the firkin, and finishes cleaner the cooler it is run. It works in keg or bottle too, but the reason to reach for it is real ale.',
    tags: ['cask', 'real-ale', 'british', 'bitter', 'conditioning'],
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-novalager',
    lab: 'CrossMyLoof',
    productCode: 'Novalager',
    name: 'Novalager',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 16, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9.5,
    sta1: null,
    description:
      'A lager strain that ferments at ale temperatures and at ale speed, for brewers who want a crisp pilsner or helles without handing a tank over to weeks of cold fermentation. Clean enough that the malt carries the beer, and rated to 9.5 % ABV on a single sachet.',
    tags: ['lager', 'warm-lager', 'fast-lager', 'pilsner', 'helles', 'clean'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // CROSSMYLOOF BEIRM — the named strains, all with a printed pitching rate
  // -------------------------------------------------------------------------
  {
    id: 'cml-atlantic',
    lab: 'CrossMyLoof',
    productCode: 'Atlantic',
    name: 'Atlantic',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 74, max: 78 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'CML describe it as US-05 with a rounder palate, and that is close: a subtle-ester house strain that leaves a little more body than a true Chico and takes pale ales and dark beers with equal ease. The narrow 19–22 °C band is the one number to respect.',
    tags: ['neutral', 'american-ale', 'pale-ale', 'workhorse', 'us-05-alternative'],
    dosageGramsPerHl: { min: 50, max: 50 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-beoir',
    lab: 'CrossMyLoof',
    productCode: 'Beòir',
    name: 'Beòir',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 75 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A Scottish and Irish ale strain built for cask: restrained attenuation, a malt-forward finish and firm settling, which is why CML point it at bitters, stouts and porters. Their sheet grades its flocculation only as "good", read here as medium-high.',
    tags: ['scottish-ale', 'irish-ale', 'cask', 'bitter', 'stout', 'porter', 'malty'],
    dosageGramsPerHl: { min: 50, max: 50 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-clipper',
    lab: 'CrossMyLoof',
    productCode: 'Clipper',
    name: 'Clipper',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'The New England strain of the range, offered as a London Ale III stand-in: soft, tropical and slow to clear. Attenuation is published as 75 % give or take five, and the pitching rate widens to 90 g/hL when the wort is big.',
    tags: ['neipa', 'hazy', 'tropical', 'juicy', 'ipa', 'london-ale-iii'],
    dosageGramsPerHl: { min: 50, max: 90 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-four',
    lab: 'CrossMyLoof',
    productCode: 'Four',
    name: 'Four',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 72, max: 75 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'The S-04 slot in the range: a quick, firm-settling English ale yeast that stops short of dry and leaves a malty backbone under bitters, browns and stouts. CML grade its flocculation as "good" rather than by step; medium-high is the reading kept here.',
    tags: ['english-ale', 'bitter', 'brown-ale', 'stout', 'malty', 's-04-alternative'],
    dosageGramsPerHl: { min: 50, max: 50 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-flushed-nun',
    lab: 'CrossMyLoof',
    productCode: 'Flushed Nun',
    name: 'Flushed Nun',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 79 },
    temperatureC: { min: 18, max: 28 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 13,
    sta1: null,
    description:
      'An abbey strain rated to 13 % ABV, the strongest ceiling in the range, and dosed lighter than the rest at 40 g/hL. Spicy and fruity through the middle of its band and more emphatically so as it warms — a tripel and quadrupel yeast that will carry a dark strong too.',
    tags: ['belgian', 'abbey', 'trappist', 'tripel', 'quadrupel', 'phenolic', 'high-gravity'],
    dosageGramsPerHl: { min: 40, max: 40 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-five',
    lab: 'CrossMyLoof',
    productCode: 'Five',
    name: 'Five',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 17, max: 28 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      "CML's clean American ale strain and their other US-05 comparison: low esters, high flocculation and a dry, hop-forward finish. The band runs warm enough to ferment through a British summer without cooling.",
    tags: ['american-ale', 'neutral', 'clean', 'ipa', 'pale-ale', 'us-05-alternative'],
    dosageGramsPerHl: { min: 50, max: 50 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-gretel',
    lab: 'CrossMyLoof',
    productCode: 'Gretel',
    name: 'Gretel',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 85, max: 90 },
    temperatureC: { min: 18, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: 11,
    sta1: true,
    description:
      'A hefeweizen yeast CML declare as Saccharomyces cerevisiae var. diastaticus, so it keeps working on dextrins and can over-carbonate in package if it is bottled before it has finished. In exchange it gives a big clove-and-banana weissbier and stays in suspension.',
    tags: ['hefeweizen', 'wheat', 'banana', 'clove', 'diastatic', 'sta1', 'weissbier'],
    dosageGramsPerHl: { min: 60, max: 60 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-haze',
    lab: 'CrossMyLoof',
    productCode: 'Haze',
    name: 'Haze',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 15, max: 20 },
    flocculation: 'low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A clean American strain chosen for what it does not do: it stays in suspension and leaves the beer permanently hazy without adding much ester of its own. CML print a single 75 % attenuation figure, so the band stored here is that number plus or minus two.',
    tags: ['hazy', 'neipa', 'american-ale', 'neutral', 'low-flocculation'],
    dosageGramsPerHl: { min: 50, max: 50 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-hell',
    lab: 'CrossMyLoof',
    productCode: 'Hell',
    name: 'Hell',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 12, max: 21 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A bottom-fermenting Berlin pilsner strain, published as Saccharomyces pastorianus and dosed heaviest in the range at 75 g/hL — CML tell you to double even that at 12 °C. They compare it to S-23, and rate the 9 % ceiling at the top of its temperature band.',
    tags: ['lager', 'pilsner', 'german-lager', 'helles', 'bottom-fermenting', 's-23-alternative'],
    dosageGramsPerHl: { min: 75, max: 75 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-hog-norsk',
    lab: 'CrossMyLoof',
    productCode: 'Høg-Norsk',
    name: 'Høg-Norsk',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 27 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A northern European ale strain that stays clean and low in esters right up to 27 °C, then settles hard; CML line it up against K-97. It suits kölsch-adjacent pales and altbiers fermented in a room rather than a fridge.',
    tags: ['clean', 'northern-european', 'altbier', 'kolsch', 'k-97-alternative'],
    dosageGramsPerHl: { min: 50, max: 50 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-house',
    lab: 'CrossMyLoof',
    productCode: 'House',
    name: 'House',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      "CML's table-beer strain and their S-33 comparison: it stops around 70 % attenuation and leaves sweetness and body behind, which flatters a low-gravity bitter or a soft IPA. Rated to 11 % ABV in spite of the modest attenuation.",
    tags: ['low-attenuation', 'table-beer', 'english-ale', 'body', 's-33-alternative'],
    dosageGramsPerHl: { min: 50, max: 50 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-kentucky',
    lab: 'CrossMyLoof',
    productCode: 'Kentucky',
    name: 'Kentucky',
    form: 'dry',
    type: 'hybrid',
    attenuationPercent: { min: 77, max: 83 },
    temperatureC: { min: 13, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A common-ale strain in the California steam mold, happy from 13 °C upward and clean across the whole band. CML aim it at commons, kölsch, cream ales and crisp pale ales; it attenuates further than most of the range and drops bright.',
    tags: ['california-common', 'steam-beer', 'cream-ale', 'kolsch', 'clean', 'hybrid'],
    dosageGramsPerHl: { min: 60, max: 60 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-midland',
    lab: 'CrossMyLoof',
    productCode: 'Midland',
    name: 'Midland',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 82 },
    temperatureC: { min: 14, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      "CML's Nottingham analogue: a Trent Valley strain that ferments cool, attenuates well and drops bright, which makes it the obvious choice for a clean bitter, a porter, or anything that has to clear in a hurry. Rated to 10 % ABV.",
    tags: [
      'english-ale',
      'bitter',
      'porter',
      'clean',
      'high-flocculation',
      'nottingham-alternative',
    ],
    dosageGramsPerHl: { min: 60, max: 60 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-monk',
    lab: 'CrossMyLoof',
    productCode: 'Monk',
    name: 'Monk',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 68, max: 77 },
    temperatureC: { min: 15, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A monastery strain kept deliberately cool — CML top the band out at 20 °C — so the ester profile stays medium rather than exuberant. Attenuation is modest for a Belgian yeast, which leaves body under a dubbel or a Belgian pale.',
    tags: ['belgian', 'abbey', 'dubbel', 'estery', 'monastery'],
    dosageGramsPerHl: { min: 50, max: 50 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-pia',
    lab: 'CrossMyLoof',
    productCode: 'Pia',
    name: 'Pia',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 75 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A New Zealand pale ale strain that keeps its own character quiet so the hops carry the beer. The modest attenuation band leaves enough malt to hold up a heavy late-hop charge, and CML grade its flocculation only as "good", read here as medium-high.',
    tags: ['pale-ale', 'new-zealand', 'hop-forward', 'clean', 'ipa'],
    dosageGramsPerHl: { min: 50, max: 50 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-the-firm',
    lab: 'CrossMyLoof',
    productCode: 'The Firm',
    name: 'The Firm',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 63, max: 68 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 8,
    sta1: null,
    description:
      'The low-attenuating end of the range, comparable to Windsor: it stops in the mid sixties and leaves real sweetness, which is what a low-alcohol pale or a fruity dark mild wants. CML print the ceiling as "8 to 9 %", and the lower figure is the one stored here.',
    tags: ['low-attenuation', 'low-alcohol', 'fruity', 'body', 'windsor-alternative'],
    dosageGramsPerHl: { min: 50, max: 50 },
    provenance: 'spec-sheet',
  },
  {
    id: 'cml-wallonia',
    lab: 'CrossMyLoof',
    productCode: 'Wallonia',
    name: 'Wallonia',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 85, max: 92 },
    temperatureC: { min: 16, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: true,
    description:
      'A diastatic saison strain — CML declare var. diastaticus on the ingredient line — that takes a beer to 92 % apparent attenuation and finishes bone dry, with soft fruit, citrus and pepper over the top. Anything bottled with it is a package-stability question.',
    tags: ['saison', 'farmhouse', 'belgian', 'diastatic', 'sta1', 'dry', 'peppery'],
    dosageGramsPerHl: { min: 50, max: 50 },
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // Kveik — attenuation published only as a word, so both are representative
  // -------------------------------------------------------------------------
  {
    id: 'cml-no-1-voss',
    lab: 'CrossMyLoof',
    productCode: 'No.1',
    name: 'No.1 (Voss)',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 25, max: 40 },
    flocculation: 'high',
    alcoholTolerancePercent: 12.5,
    sta1: null,
    description:
      "CML's Voss kveik: semi-neutral with an orange lift, working anywhere from 25 to 40 °C and finishing in two to four days at the top of that. Feed it nutrient or it will stress and throw esters. CML publish attenuation only as 'medium to high', so the band here is typical for the species rather than theirs.",
    tags: ['kveik', 'voss', 'fast-ferment', 'orange', 'high-temperature', 'high-flocculation'],
    provenance: 'representative',
  },
  {
    id: 'cml-horn',
    lab: 'CrossMyLoof',
    productCode: 'Horn',
    name: 'Horn (Hornindal)',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 25, max: 40 },
    flocculation: 'high',
    alcoholTolerancePercent: 12.5,
    sta1: null,
    description:
      "The Hornindal half of the kveik pair, more tropical than Voss and every bit as quick — CML put full attenuation at two to four days when it is held near 40 °C. As with No.1, attenuation is published only as a word, so the band stored here is typical rather than the maker's.",
    tags: ['kveik', 'hornindal', 'tropical', 'fast-ferment', 'high-temperature'],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Souring pitches — nominal attenuation, following escarpment's lacto blend
  // -------------------------------------------------------------------------
  {
    id: 'cml-sour-3-5',
    lab: 'CrossMyLoof',
    productCode: 'Sour 3.5',
    name: 'Sour 3.5',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 18, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: 7.2,
    sta1: null,
    description:
      'Dry Lachancea thermotolerans, pitched alone to make lactic acid while it starts the fermentation; once pH reaches 3.0–3.5, a day to four days later, a conventional yeast goes in to finish the beer. CML publish neither attenuation nor flocculation, so both figures here are nominal and describe the souring step rather than a gravity to plan around.',
    tags: ['sour', 'lachancea', 'berliner-weisse', 'gose', 'souring-pitch'],
    dosageGramsPerHl: { min: 130, max: 130 },
    provenance: 'representative',
  },
  {
    id: 'cml-new-sour-3-5',
    lab: 'CrossMyLoof',
    productCode: 'New Sour 3.5',
    name: 'New Sour 3.5',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 34, max: 36 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A dehydrated Lactobacillus plantarum pitch for kettle souring or a mixed fermentation, dropping pH quickly at 34–36 °C and leaving a citrus note behind. It is hop-intolerant, so leave the kettle bitterness at zero. It makes acid rather than alcohol; the attenuation and flocculation figures are nominal.',
    tags: ['sour', 'lactobacillus', 'kettle-sour', 'gose', 'berliner-weisse', 'bacteria'],
    dosageGramsPerHl: { min: 20, max: 20 },
    provenance: 'representative',
  },
];
